import { Check } from "lucide-react";

import { cn } from "@/modern/utils";

import {
  ExtractionType,
  ExtractionResults,
  ExtractDatesResponse,
  ExtractChecklistResponse,
  ExtractLocationResponse,
  ExtractBudgetResponse,
  ExtractClientResponse,
  ExtractNotesResponse,
  EXTRACTION_OPERATIONS,
} from "./types";

/** Returns a short label for the cached result count, e.g. "3 found" or "Extracted". */
const getCachedLabel = (type: ExtractionType, results: ExtractionResults): string => {
  switch (type) {
    case "dates": {
      const count = (results as ExtractDatesResponse).date_cards?.length ?? 0;
      return count === 1 ? "1 date" : `${count} dates`;
    }
    case "summary":
      return "Extracted";
    case "checklist": {
      const count = (results as ExtractChecklistResponse).checklists?.length ?? 0;
      return count === 1 ? "1 list" : `${count} lists`;
    }
    case "location": {
      const count = (results as ExtractLocationResponse).locations?.length ?? 0;
      return count === 1 ? "1 location" : `${count} locations`;
    }
    case "budget": {
      const count = (results as ExtractBudgetResponse).budgets?.length ?? 0;
      return count === 1 ? "1 budget" : `${count} budgets`;
    }
    case "client": {
      const count = (results as ExtractClientResponse).clients?.length ?? 0;
      return count === 1 ? "1 client" : `${count} clients`;
    }
    case "notes": {
      const count = (results as ExtractNotesResponse).notes?.length ?? 0;
      return count === 1 ? "1 note" : `${count} notes`;
    }
    default:
      return "Extracted";
  }
};

interface ExtractionOperationListProps {
  onSelectOperation: (type: ExtractionType) => void;
  cachedResults?: Partial<Record<ExtractionType, ExtractionResults>>;
}

export const ExtractionOperationList = ({ onSelectOperation, cachedResults = {} }: ExtractionOperationListProps) => {
  return (
    <div className="flex flex-col gap-2 p-4">
      {EXTRACTION_OPERATIONS.map((operation) => {
        const Icon = operation.icon;
        const cached = cachedResults[operation.type];

        return (
          <button
            key={operation.type}
            type="button"
            className={cn(
              "flex items-center gap-3 w-full p-3 rounded-swivu-base border border-border",
              "hover:bg-accent hover:border-primary/30 active:bg-accent/80",
              "transition-all duration-200 text-left group",
              cached && "border-primary/20 bg-primary/[0.03]"
            )}
            onClick={() => onSelectOperation(operation.type)}
          >
            <div className={cn(
              "flex items-center justify-center w-9 h-9 rounded-md shrink-0 transition-colors",
              cached
                ? "bg-green-500/10 text-green-600 dark:text-green-400"
                : "bg-primary/10 text-primary group-hover:bg-primary/15"
            )}>
              {cached ? <Check className="w-4 h-4" /> : Icon && <Icon className="w-4 h-4" />}
            </div>
            <div className="flex flex-col min-w-0 flex-1">
              <span className="text-sm font-medium text-foreground">{operation.label}</span>
              <span className="text-xs text-muted-foreground">
                {cached ? getCachedLabel(operation.type, cached) : operation.description}
              </span>
            </div>
            {cached && (
              <span className="text-xs text-muted-foreground shrink-0">View →</span>
            )}
          </button>
        );
      })}
    </div>
  );
};
